/**
 * Server-side partner search for /redeem (map-listed redemption plan v4 §3.3, release R1).
 *
 * Replaces the preloaded dropdown for ALL / MULTIPLE offer vouchers: the old list came from
 * get_redemption_organizations through PostgREST, which silently stopped at 1,000 rows, so partners later in the
 * alphabet could not select themselves. This searches the same eligible set on the server
 * (redeem-picker-search), 25 at a time, with "Show more".
 *
 * Behaviour: debounced typing; every request carries a sequence number and only the LATEST query's response is
 * applied (a slow earlier response can never overwrite newer results); "Show more" appends the next page for the
 * same query. Instructor street addresses come back NULL from the server and are simply not shown.
 *
 * Round-4 audit R4-1 (2026-09-30): a change of query, voucher, selection or a clear INVALIDATES immediately --
 * before the debounce fires -- so an in-flight response for the old text can never appear under the new text,
 * and the old results/cursor are cleared. The cursor is stored with the query it belongs to and "Show more" is
 * offered only when that query is the current text, so a new term is never sent with an old term's cursor.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  searchRedemptionEntries,
  type RedemptionSearchCursor,
  type RedemptionSearchEntry,
} from "../../services/redemption.service";

export interface RedemptionEntrySearchProps {
  voucherCode: string;
  selected: RedemptionSearchEntry | null;
  onSelect: (entry: RedemptionSearchEntry | null) => void;
  /** Injected in tests. */
  search?: typeof searchRedemptionEntries;
  debounceMs?: number;
}

export function entryLabel(e: RedemptionSearchEntry): string {
  const place = [e.city, e.state].filter(Boolean).join(", ");
  return place ? `${e.name} - ${place}` : e.name;
}

const RedemptionEntrySearch: React.FC<RedemptionEntrySearchProps> = ({
  voucherCode,
  selected,
  onSelect,
  search = searchRedemptionEntries,
  debounceMs = 250,
}) => {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<RedemptionSearchEntry[]>([]);
  // The cursor is only meaningful for the query that produced it.
  const [cursor, setCursor] = useState<{
    q: string;
    at: RedemptionSearchCursor | null;
  } | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const seq = useRef(0);
  const boxRef = useRef<HTMLDivElement>(null);

  // Drop anything in flight and every trace of the previous query's results.
  const invalidate = useCallback(() => {
    seq.current += 1;
    setItems([]);
    setCursor(null);
    setHasMore(false);
    setLoading(false);
    setFailed(false);
  }, []);

  const runSearch = useCallback(
    async (q: string, after: RedemptionSearchCursor | null) => {
      const mine = ++seq.current;
      setLoading(true);
      setFailed(false);
      const page = await search(voucherCode, q, after);
      if (mine !== seq.current) {
        return; // a newer query (or page request) superseded this one
      }
      setLoading(false);
      if (page.status !== "ok") {
        setFailed(true);
        if (!after) {
          setItems([]);
        }
        setHasMore(false);
        return;
      }
      setItems((prev) => (after ? [...prev, ...page.items] : page.items));
      setCursor({ q, at: page.next_cursor });
      setHasMore(page.has_more);
    },
    [search, voucherCode],
  );

  // A different voucher is a different eligible set: start over.
  useEffect(() => {
    invalidate();
  }, [voucherCode, invalidate]);

  // Debounced search whenever the typed term changes while the list is open.
  useEffect(() => {
    if (!open || selected) {
      return;
    }
    const t = setTimeout(() => {
      void runSearch(term, null);
    }, debounceMs);
    return () => clearTimeout(t);
  }, [term, open, selected, runSearch, debounceMs]);

  useEffect(() => {
    const onDown = (ev: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        aria-label="Search organizations"
        value={selected ? entryLabel(selected) : term}
        onChange={(e) => {
          invalidate();
          onSelect(null);
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search by name, city, state, ZIP or facility ID..."
        className="w-full pl-4 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
      />
      {selected && (
        <button
          type="button"
          aria-label="Clear selection"
          onClick={() => {
            invalidate();
            onSelect(null);
            setTerm("");
            setOpen(true);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
      {open && !selected && (
        <div
          role="listbox"
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto"
        >
          {items.map((e) => (
            <button
              key={e.entry_id}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => {
                seq.current += 1; // nothing still in flight may repaint after a choice
                onSelect(e);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors text-gray-900"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{e.name}</span>
                {e.type_label && (
                  <span className="text-xs text-gray-500 ml-2">
                    {e.type_label}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500">
                {e.address && <span>{e.address}, </span>}
                {(e.city || e.state) && (
                  <span>{[e.city, e.state].filter(Boolean).join(", ")}</span>
                )}
                {e.zip && <span> {e.zip}</span>}
              </div>
            </button>
          ))}
          {loading && (
            <div className="px-4 py-3 text-sm text-gray-500">Searching…</div>
          )}
          {!loading && failed && (
            <div className="px-4 py-3 text-sm text-red-600">
              Search is temporarily unavailable. Please try again.
            </div>
          )}
          {!loading && !failed && items.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500">
              {term
                ? `No organizations found matching "${term}"`
                : "No organizations found"}
            </div>
          )}
          {!loading && hasMore && cursor?.q === term && (
            <button
              type="button"
              onClick={() => void runSearch(term, cursor.at)}
              className="w-full px-4 py-2 text-sm font-medium text-purple-700 border-t hover:bg-purple-50"
            >
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RedemptionEntrySearch;
